// §10.2 — the shell's own drag overlay.
//
// "The shell tracks the drag on its overlay (**apps cannot see each other's DOM**), highlights
// valid targets from their manifest `accepts`, and delivers on drop."
//
// The parenthesis is the reason this exists, and it is not a preference: a drag cannot be dropped
// *into* a cross-origin iframe, because the frame's own document owns its drop targets and never
// hears about a drag begun outside it. So the shell puts a surface **over** the frames for the
// duration of the drag, and that surface is the drop target for every window on the desktop.
//
// It is four bands, not one sheet, with the **source window's rect punched out**: Explorer's own
// drag-a-file-into-a-folder happens inside its window and must keep working exactly as it did.
// Everything outside the window the drag started in belongs to the shell.
import { useEffect, useRef, useState } from 'react';
import type { Rect } from '@hangar/shared';
import { useKernel } from '@/kernel/react';

/** Mark the hovered `.win-host` so the shell can outline a valid target (§10.2). */
function markTarget(winId: string | null): void {
  for (const host of document.querySelectorAll<HTMLElement>('.win-host[data-drop-target]')) {
    delete host.dataset['dropTarget'];
  }
  if (winId === null) return;
  const host = document.querySelector<HTMLElement>(
    `.win-host[data-window-id="${CSS.escape(winId)}"]`,
  );
  if (host) host.dataset['dropTarget'] = 'true';
}

/**
 * The four bands around `hole`, so the source window keeps its own drag behaviour.
 *
 * With **no** known source the layer covers nothing at all. A drag the shell cannot place is a
 * drag it must not intercept: covering the desktop on a guess would break whichever app started
 * it, and failing to offer a cross-window drop is a missing feature rather than a broken one.
 */
function bandsAround(hole: Rect | null): Array<{ key: string; style: React.CSSProperties }> {
  if (!hole) return [];
  return [
    { key: 'top', style: { insetInlineStart: 0, insetBlockStart: 0, width: '100%', height: hole.y } },
    {
      key: 'bottom',
      style: {
        insetInlineStart: 0,
        insetBlockStart: hole.y + hole.h,
        width: '100%',
        bottom: 0,
      },
    },
    {
      key: 'left',
      style: { insetInlineStart: 0, insetBlockStart: hole.y, width: hole.x, height: hole.h },
    },
    {
      key: 'right',
      style: {
        insetInlineStart: hole.x + hole.w,
        insetBlockStart: hole.y,
        right: 0,
        height: hole.h,
      },
    },
  ];
}

export function DragLayer() {
  const kernel = useKernel();
  const [active, setActive] = useState(false);
  const [label, setLabel] = useState('');
  const [hole, setHole] = useState<Rect | null>(null);
  const ghost = useRef<HTMLDivElement | null>(null);
  /** The window a drag started in, read off the DOM at `dragstart` (D-136). */
  const source = useRef<Rect | null>(null);

  // **Where the drag started is a DOM fact, so it is read from the DOM.** Asking which window is
  // focused would be a guess: a drag begins on an element, and that element's `.win-host` is the
  // answer whether or not focus followed. Getting this wrong puts the overlay over the source
  // window and silently breaks that app's own drag — which is exactly what it did once.
  useEffect(() => {
    const onDragStart = (event: DragEvent): void => {
      const target = event.target as HTMLElement | null;
      const host = target?.closest?.('.win-host') ?? null;
      if (!host) {
        source.current = null;
        return;
      }
      const box = host.getBoundingClientRect();
      source.current = { x: box.left, y: box.top, w: box.width, h: box.height };
    };
    document.addEventListener('dragstart', onDragStart, true);
    return () => document.removeEventListener('dragstart', onDragStart, true);
  }, []);

  useEffect(
    () =>
      kernel.drag.subscribe(() => {
        const state = kernel.drag.state();
        setActive(state !== null);
        setLabel(state?.label ?? '');
        setHole(state === null ? null : source.current);
        if (state === null) markTarget(null);
      }),
    [kernel],
  );

  // A drag that ends anywhere the shell did not see — outside the window, on a native target —
  // must still clear the state, or the desktop stays covered by an invisible sheet.
  useEffect(() => {
    if (!active) return;
    const onEnd = (): void => kernel.drag.cancel();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') kernel.drag.cancel();
    };
    window.addEventListener('dragend', onEnd);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('dragend', onEnd);
      window.removeEventListener('keydown', onKey);
    };
  }, [active, kernel]);

  if (!active) return null;

  /** Which window is under the pointer. The bands are in the way, so they step aside for one call. */
  const hitTest = (x: number, y: number): string | null => {
    const layer = document.querySelector<HTMLElement>('.drag-layer');
    const previous = layer?.style.pointerEvents ?? '';
    if (layer) layer.style.pointerEvents = 'none';
    const element = document.elementFromPoint(x, y);
    if (layer) layer.style.pointerEvents = previous;
    return element?.closest<HTMLElement>('.win-host')?.dataset['windowId'] ?? null;
  };

  const track = (event: React.DragEvent): void => {
    event.preventDefault();
    const winId = hitTest(event.clientX, event.clientY);
    const accepted = kernel.drag.hover(winId);
    event.dataTransfer.dropEffect = accepted ? 'copy' : 'none';
    markTarget(accepted ? winId : null);
    if (ghost.current) {
      // `transform`, not `left`/`top` — the same rule §5.2 applies to a window drag.
      ghost.current.style.transform = `translate(${event.clientX + 14}px, ${event.clientY + 14}px)`;
    }
  };

  return (
    <div className="drag-layer" data-testid="drag-layer">
      {bandsAround(hole).map((band) => (
        <div
          key={band.key}
          className="drag-band"
          data-testid={`drag-band-${band.key}`}
          style={band.style}
          onDragOver={track}
          onDragEnter={track}
          onDrop={(event) => {
            event.preventDefault();
            kernel.drag.hover(hitTest(event.clientX, event.clientY));
            kernel.drag.drop();
            markTarget(null);
          }}
        />
      ))}
      <div className="drag-ghost" data-testid="drag-ghost" ref={ghost}>
        {label}
      </div>
    </div>
  );
}
